import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  DragEvent,
  ChangeEvent,
} from "react";
import { Button } from "../ui/button";
import toast from "react-hot-toast";
import { isArray } from "lodash";

interface Props {
  className?: string;
  onChange: (files: File[]) => void;
  disabled?: boolean;
  description?: string;
}

export interface DragUploadRef {
  reset: () => void;
}

export const DragUpload = forwardRef<DragUploadRef, Props>(
  (
    {
      onChange,
      className,
      disabled = false,
      description = "Kích thước tối đa 100MB",
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (folderInputRef.current) folderInputRef.current.value = "";
      },
    }));

    const checkHeic = (file: File) => {
      const isHeic = file.name.toLowerCase().endsWith(".heic");
      return isHeic;
    };

    const handleChangeFiles = (files: File[]) => {
      if (!isArray(files) || !files.length) return;

      let shouldNotifyHeic = false;

      const result: File[] = [];

      files.forEach((file) => {
        const isHeic = checkHeic(file);

        if (file.name.startsWith(".")) return;

        if (isHeic) return (shouldNotifyHeic = true);

        result.push(file);
      });

      if (shouldNotifyHeic) {
        toast.error("không hổ trợ định dạng HEIC");
      }

      onChange(result);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
      handleDropHandler(e);
    };

    const handleDropHandler = async (e: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();

      const items = e.dataTransfer.items;
      const filePromises: Promise<File[]>[] = [];

      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          filePromises.push(traverseFileTree(entry));
        }
      }

      const nestedFiles = await Promise.all(filePromises);
      const allFiles = nestedFiles.flat();
      handleChangeFiles(allFiles);
    };

    const traverseFileTree = (
      item: FileSystemEntry,
      path = ""
    ): Promise<File[]> => {
      return new Promise((resolve) => {
        if (item.isFile) {
          (item as FileSystemFileEntry).file((file) => {
            file = new File([file], file.name);
            resolve([file]);
          });
        } else if (item.isDirectory) {
          const dirReader = (item as FileSystemDirectoryEntry).createReader();
          dirReader.readEntries(async (entries) => {
            const promises = entries.map((entry) =>
              traverseFileTree(entry, path + item.name + "/")
            );
            const nestedFiles = await Promise.all(promises);
            resolve(nestedFiles.flat());
          });
        }
      });
    };

    const handleFileUpload = () => {
      if (!disabled) fileInputRef.current?.click();
    };

    const handleFolderUpload = () => {
      if (!disabled) folderInputRef.current?.click();
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      handleChangeFiles(e.target.files ? Array.from(e.target.files) : []);
      e.target.value = "";
    };

    return (
      <div className={className}>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            disabled
              ? "border-gray-300 cursor-not-allowed"
              : "border-gray-300 hover:border-blue-400"
          }`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="text-xs font-bold text-gray-700 mb-0.5">
            Kéo tài liệu vào đây hoặc nhấn để tải lên từ thiết bị
          </div>

          <div className="text-xs text-gray-500 mb-2">{description}</div>

          <div className="flex gap-4 justify-center">
            <Button
              onClick={handleFileUpload}
              variant="outline"
              className="py-1.5 px-3"
              disabled={disabled}
            >
              <img
                className="w-[18px] h-[18px] ml-2"
                src="/icon-upload-file.svg"
                alt="file"
              />
              Tải tài liệu
            </Button>
            <Button
              onClick={handleFolderUpload}
              variant="outline"
              className="py-1.5 px-3"
              disabled={disabled}
            >
              <img
                className="w-[18px] h-[18px] ml-2"
                src="/icon-upload-folder.svg"
                alt="folder"
              />
              Tải thư mục
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleInputChange}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          // @ts-ignore: webkitdirectory is a valid attribute
          webkitdirectory=""
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
    );
  }
);

DragUpload.displayName = "DragUpload";
